import { useState, useCallback } from 'react';
import { Upload, ChevronRight, ChevronLeft, Loader2, FileSpreadsheet, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PriceBookItemImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImport: (items: PriceBookItemImportRow[]) => Promise<void>;
}

export interface PriceBookItemImportRow {
  itemcode: string;
  description: string;
  price?: number;
  unit?: string;
  discount?: number;
  supplier_itemcode?: string;
  supplier_description?: string;
  is_active?: boolean;
  locked_when_used?: boolean;
}

const ITEM_FIELDS = [
  { key: 'itemcode', label: 'Item Code', required: true },
  { key: 'description', label: 'Description', required: true },
  { key: 'price', label: 'Price', required: false },
  { key: 'unit', label: 'Unit of Measure', required: false },
  { key: 'discount', label: 'Discount %', required: false },
  { key: 'supplier_itemcode', label: 'Supplier Item Code', required: false },
  { key: 'supplier_description', label: 'Supplier Description', required: false },
  { key: 'is_active', label: 'Is Active', required: false },
  { key: 'locked_when_used', label: 'Locked When Used', required: false },
];

type Step = 'upload' | 'mapping' | 'preview';

export default function PriceBookItemImportWizard({ open, onOpenChange, onImport }: PriceBookItemImportWizardProps) {
  const [step, setStep] = useState<Step>('upload');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [isImporting, setIsImporting] = useState(false);
  const [fileName, setFileName] = useState<string>('');

  const resetWizard = useCallback(() => {
    setStep('upload');
    setCsvHeaders([]);
    setCsvData([]);
    setColumnMapping({});
    setFileName('');
  }, []);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetWizard();
    }
    onOpenChange(newOpen);
  };

  const parseCSV = (text: string): { headers: string[]; rows: string[][] } => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const parseRow = (row: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        if (char === '"') {
          if (inQuotes && row[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(parseRow);

    return { headers, rows };
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const { headers, rows } = parseCSV(text);
      
      setCsvHeaders(headers);
      setCsvData(rows);

      // Auto-map columns based on similar names
      const autoMapping: Record<string, string> = {};
      headers.forEach((header) => {
        const headerLower = header.toLowerCase().replace(/[^a-z]/g, '');
        ITEM_FIELDS.forEach((field) => {
          const fieldLower = field.label.toLowerCase().replace(/[^a-z]/g, '');
          const keyLower = field.key.toLowerCase();
          if (headerLower.includes(fieldLower) || headerLower.includes(keyLower) || fieldLower.includes(headerLower)) {
            if (!autoMapping[field.key]) {
              autoMapping[field.key] = header;
            }
          }
        });
      });
      setColumnMapping(autoMapping);
      setStep('mapping');
    };
    reader.readAsText(file);
  };

  const handleMappingChange = (fieldKey: string, csvHeader: string) => {
    setColumnMapping((prev) => ({
      ...prev,
      [fieldKey]: csvHeader === '__none__' ? '' : csvHeader,
    }));
  };

  const getMappedData = (): PriceBookItemImportRow[] => {
    return csvData.map((row) => {
      const item: PriceBookItemImportRow = { itemcode: '', description: '' };
      ITEM_FIELDS.forEach((field) => {
        const csvHeader = columnMapping[field.key];
        if (csvHeader) {
          const headerIndex = csvHeaders.indexOf(csvHeader);
          if (headerIndex !== -1) {
            const value = row[headerIndex] || '';
            if (field.key === 'price' || field.key === 'discount') {
              // Clean the value: remove currency symbols, commas, etc.
              const cleanValue = value.replace(/[$,\s]/g, '');
              const numVal = parseFloat(cleanValue);
              if (!isNaN(numVal) && isFinite(numVal)) {
                // Clamp to reasonable range to prevent numeric overflow
                const maxVal = field.key === 'price' ? 99999999.99 : 100;
                (item as any)[field.key] = Math.min(Math.max(numVal, 0), maxVal);
              }
            } else if (field.key === 'is_active' || field.key === 'locked_when_used') {
              (item as any)[field.key] = value.toLowerCase() === 'true' || value === '1' || value.toLowerCase() === 'yes';
            } else {
              (item as any)[field.key] = value;
            }
          }
        }
      });
      return item;
    }).filter((v) => v.itemcode.trim() !== '' && v.description.trim() !== '');
  };

  const handleImport = async () => {
    setIsImporting(true);
    try {
      const items = getMappedData();
      await onImport(items);
      handleOpenChange(false);
    } catch (error) {
      console.error('Import error:', error);
      // Error is handled by the onImport mutation's onError callback
    } finally {
      setIsImporting(false);
    }
  };

  const isRequiredMapped = !!columnMapping.itemcode && !!columnMapping.description;
  const mappedData = step === 'preview' ? getMappedData() : [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import Price Book Items</DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-4 border-b">
          {(['upload', 'mapping', 'preview'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s
                    ? 'bg-primary text-primary-foreground'
                    : ['upload', 'mapping', 'preview'].indexOf(step) > i
                    ? 'bg-primary/20 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {['upload', 'mapping', 'preview'].indexOf(step) > i ? (
                  <Check className="w-4 h-4" />
                ) : (
                  i + 1
                )}
              </div>
              <span className="text-sm hidden sm:inline">
                {s === 'upload' ? 'Upload' : s === 'mapping' ? 'Map Columns' : 'Preview'}
              </span>
              {i < 2 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-auto py-4">
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <FileSpreadsheet className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Upload CSV File</h3>
              <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
                Select a CSV file with price book item data. The first row should contain column headers.
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                  <Upload className="w-5 h-5" />
                  Choose File
                </div>
              </label>
            </div>
          )}

          {step === 'mapping' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg mb-4">
                <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm">{fileName}</span>
                <span className="text-sm text-muted-foreground">
                  ({csvData.length} rows, {csvHeaders.length} columns)
                </span>
              </div>

              <p className="text-sm text-muted-foreground">
                Map the columns from your CSV file to the item fields below.
              </p>

              <div className="space-y-3">
                {ITEM_FIELDS.map((field) => (
                  <div key={field.key} className="grid grid-cols-2 gap-4 items-center">
                    <Label className="flex items-center gap-1">
                      {field.label}
                      {field.required && <span className="text-destructive">*</span>}
                    </Label>
                    <Select
                      value={columnMapping[field.key] || '__none__'}
                      onValueChange={(value) => handleMappingChange(field.key, value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Not mapped --</SelectItem>
                        {csvHeaders
                          .filter((header) => header.trim() !== '')
                          .map((header) => (
                            <SelectItem key={header} value={header}>
                              {header}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {!isRequiredMapped && (
                <p className="text-sm text-destructive mt-4">
                  Item Code and Description are required. Please map columns to them.
                </p>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Review the data below before importing. {mappedData.length} items will be imported.
              </p>

              <ScrollArea className="h-[300px] border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2 font-medium">Item Code</th>
                      <th className="text-left p-2 font-medium">Description</th>
                      <th className="text-right p-2 font-medium">Price</th>
                      <th className="text-left p-2 font-medium">Unit</th>
                      <th className="text-right p-2 font-medium">Discount</th>
                      <th className="text-center p-2 font-medium">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedData.slice(0, 50).map((item, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 font-medium">{item.itemcode}</td>
                        <td className="p-2">{item.description}</td>
                        <td className="p-2 text-right font-mono">{item.price?.toFixed(2) || '0.00'}</td>
                        <td className="p-2">{item.unit || '—'}</td>
                        <td className="p-2 text-right font-mono">{item.discount?.toFixed(2) || '0.00'}%</td>
                        <td className="p-2 text-center">{item.is_active !== false ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>

              {mappedData.length > 50 && (
                <p className="text-sm text-muted-foreground">
                  Showing first 50 of {mappedData.length} items.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              if (step === 'mapping') setStep('upload');
              else if (step === 'preview') setStep('mapping');
            }}
            disabled={step === 'upload'}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>

            {step === 'mapping' && (
              <Button onClick={() => setStep('preview')} disabled={!isRequiredMapped}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}

            {step === 'preview' && (
              <Button onClick={handleImport} disabled={isImporting || mappedData.length === 0}>
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    Import {mappedData.length} Items
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
